import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { EmptyState } from "../components/ui/EmptyState";
import { Skeleton } from "../components/ui/Skeleton";
import { Sparkline } from "../features/activity/Sparkline";
import { EventGlyph } from "../features/community/EventGlyph";
import { useMyActivityStats, useMyRecentActivity } from "../features/activity/hooks";
import { useSession } from "../features/auth/hooks";
import { formatRelative } from "../features/reports/relativeTime";

import type { RecentActivityEvent } from "../features/activity/api";

export function ActivityRoute() {
  const { user } = useSession();

  if (!user) return <UnauthEmptyState />;

  return <AuthedActivity />;
}

function UnauthEmptyState() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 pt-3">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-fg">{t("activity.title")}</h1>
      </header>
      <EmptyState
        glyph="⚡"
        eyebrow={t("activity.unauth_eyebrow")}
        title={t("activity.unauth_title")}
        body={t("activity.unauth_body")}
        action={
          <Link to="/auth/login" className="btn-volt inline-flex items-center px-4 py-2 text-sm">
            {t("activity.unauth_cta")}
          </Link>
        }
      />
    </div>
  );
}

function AuthedActivity() {
  const { t } = useTranslation();
  const stats = useMyActivityStats();

  if (stats.isError) {
    return (
      <div className="pt-3">
        <EmptyState
          tone="ember"
          glyph="⚠"
          eyebrow={t("activity.empty_eyebrow_error")}
          title={t("activity.stats_failed")}
          body={t("activity.stats_failed_body")}
          action={
            <button
              type="button"
              onClick={() => stats.refetch()}
              className="btn-ghost inline-flex items-center px-4 py-2 text-sm"
            >
              {t("common.retry")}
            </button>
          }
        />
      </div>
    );
  }

  if (stats.isLoading || !stats.data) {
    return (
      <div className="space-y-4 pt-3">
        <div className="space-y-4 md:grid md:grid-cols-3 md:gap-3 md:space-y-0">
          <Skeleton className="h-32 w-full md:h-20" />
          <div className="grid grid-cols-2 gap-3 md:contents">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const { reports_submitted, stations_verified, issues_reported, reports_sparkline } = stats.data;

  return (
    <div className="space-y-4 pt-3">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-fg">{t("activity.title")}</h1>
        <p className="text-sm text-fg-mute">{t("activity.subtitle")}</p>
      </header>

      <div
        data-testid="activity-stat-row"
        className="space-y-4 md:grid md:grid-cols-3 md:items-stretch md:gap-3 md:space-y-0"
      >
        <HeroCard
          label={t("activity.hero_reports_label")}
          count={reports_submitted}
          sparkline={reports_sparkline}
        />

        <div className="grid grid-cols-2 gap-3 md:contents">
          <SecondaryCard
            label={t("activity.card_verified")}
            value={stations_verified}
            accent="muted"
          />
          <SecondaryCard
            label={t("activity.card_issues")}
            value={issues_reported}
            accent={issues_reported > 0 ? "amber" : "muted"}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          t("activity.v2_slot_energy"),
          t("activity.v2_slot_carbon"),
          t("activity.v2_slot_points"),
        ].map((label) => (
          <div
            key={label}
            className="rounded-lg border border-dashed border-line bg-bg-deep p-3 text-center text-[10px] text-fg-faint"
          >
            {label}
          </div>
        ))}
      </div>

      <RecentActivityList />
    </div>
  );
}

function HeroCard({
  label,
  count,
  sparkline,
}: {
  label: string;
  count: number;
  sparkline: number[];
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="label-caps text-fg-mute">{label}</p>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <span data-testid="activity-hero-count" className="data text-fg">
          {count}
        </span>
        <Sparkline data={sparkline} className="text-volt" />
      </div>
    </div>
  );
}

function SecondaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: "amber" | "muted";
}) {
  const colorClass = accent === "amber" ? "text-amber" : "text-fg";
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <p className="label-caps text-fg-mute">{label}</p>
      <p className={`num mt-1 text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );
}

function RecentActivityList() {
  const { t } = useTranslation();
  const history = useMyRecentActivity();

  if (history.isLoading) return <Skeleton className="h-24 w-full" />;
  if (history.isError) {
    return (
      <EmptyState
        tone="ember"
        glyph="⚠"
        title={t("activity.history_failed")}
        body={t("activity.history_failed_body")}
      />
    );
  }
  if (!history.data || history.data.length === 0) {
    return (
      <EmptyState
        glyph="📋"
        eyebrow={t("activity.history_empty_eyebrow")}
        title={t("activity.history_empty")}
        body={t("activity.history_empty_body")}
        action={
          <Link to="/" className="btn-volt inline-flex items-center px-4 py-2 text-sm">
            {t("activity.history_empty_cta")}
          </Link>
        }
      />
    );
  }

  return (
    <section>
      <h2 className="label-caps mb-2 text-fg-mute">{t("activity.history_heading")}</h2>
      <ul className="space-y-2">
        {history.data.map((event) => (
          <HistoryItem key={`${event.type}-${event.id}`} event={event} />
        ))}
      </ul>
    </section>
  );
}

function HistoryItem({ event }: { event: RecentActivityEvent }) {
  return (
    <li className="flex items-center gap-3 rounded-lg border border-line bg-surface p-3">
      <EventGlyph type={event.type} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-fg">{event.station_name ?? "—"}</p>
        <p className="num text-xs text-fg-faint">{formatRelative(event.created_at)}</p>
      </div>
    </li>
  );
}
